import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { getMerchants, createMerchant, getMerchant } from '../../api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '../../components/ui/dialog';
import { Search, Plus, Store, Phone, MapPin } from 'lucide-react';
import { toast } from 'sonner';

const Merchants = ({ user, setUser }) => {
  const [merchants, setMerchants] = useState([]);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', email: '', address: '', state: '', city: '', pincode: '', business_type: '', gstin: '', pan: '' });

  const load = async () => {
    try { const r = await getMerchants({ search }); setMerchants(r.data); }
    catch { toast.error('Failed to load merchants'); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const submit = async () => {
    try { await createMerchant(form); toast.success('Merchant added'); setOpen(false); setForm({ name: '', phone: '', email: '', address: '', state: '', city: '', pincode: '', business_type: '', gstin: '', pan: '' }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const viewDetail = async (id) => {
    try { const r = await getMerchant(id); setDetail(r.data); setDetailOpen(true); }
    catch { toast.error('Failed to load merchant'); }
  };

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Merchants</h1>
            <p className="text-sm text-slate-500">Onboarded merchants and their deployed assets</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="add-merchant-btn"><Plus className="h-4 w-4 mr-1" />Onboard Merchant</Button></DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Onboard Merchant</DialogTitle></DialogHeader>
              <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="m-name" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Phone *</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} data-testid="m-phone" /></div>
                  <div><Label>Email</Label><Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
                </div>
                <div><Label>Address</Label><Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} /></div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>City</Label><Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} /></div>
                  <div><Label>State</Label><Input value={form.state} onChange={e => setForm({ ...form, state: e.target.value })} /></div>
                  <div><Label>Pincode</Label><Input value={form.pincode} onChange={e => setForm({ ...form, pincode: e.target.value })} /></div>
                </div>
                <div><Label>Business Type</Label><Input value={form.business_type} onChange={e => setForm({ ...form, business_type: e.target.value })} placeholder="e.g. Kirana, Restaurant, Pharmacy" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>GSTIN (optional)</Label><Input value={form.gstin} onChange={e => setForm({ ...form, gstin: e.target.value })} /></div>
                  <div><Label>PAN (optional)</Label><Input value={form.pan} onChange={e => setForm({ ...form, pan: e.target.value })} /></div>
                </div>
              </div>
              <DialogFooter><Button onClick={submit} disabled={!form.name || !form.phone} data-testid="m-save">Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-2 top-3 h-4 w-4 text-slate-400" />
                <Input className="pl-8" placeholder="Search by name, code, or phone..." value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && load()} data-testid="search-merchant" />
              </div>
              <Button variant="outline" onClick={load} size="sm">Search</Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {merchants.map(m => (
                <Card key={m.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => viewDetail(m.id)} data-testid={`merchant-card-${m.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-emerald-100"><Store className="h-4 w-4 text-emerald-700" /></div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm text-slate-900 truncate">{m.name}</p>
                        <p className="text-xs text-slate-500 font-mono">{m.merchant_code}</p>
                        <div className="flex items-center gap-1 mt-1 text-xs text-slate-600"><Phone className="h-3 w-3" />{m.phone}</div>
                        {(m.city || m.state) && <div className="flex items-center gap-1 text-xs text-slate-600"><MapPin className="h-3 w-3" />{[m.city, m.state].filter(Boolean).join(', ')}</div>}
                        {m.business_type && <span className="inline-block mt-2 px-2 py-0.5 text-xs rounded bg-slate-100 text-slate-700">{m.business_type}</span>}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {merchants.length === 0 && <div className="col-span-full p-8 text-center text-slate-500">No merchants yet</div>}
            </div>
          </CardContent>
        </Card>

        <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>{detail?.name}</DialogTitle></DialogHeader>
            {detail && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><Label className="text-xs text-slate-500">Code</Label><p className="font-mono">{detail.merchant_code}</p></div>
                  <div><Label className="text-xs text-slate-500">Phone</Label><p>{detail.phone}</p></div>
                  <div><Label className="text-xs text-slate-500">Email</Label><p>{detail.email || '-'}</p></div>
                  <div><Label className="text-xs text-slate-500">Business</Label><p>{detail.business_type || '-'}</p></div>
                  <div className="col-span-2"><Label className="text-xs text-slate-500">Address</Label><p>{[detail.address, detail.city, detail.state, detail.pincode].filter(Boolean).join(', ') || '-'}</p></div>
                  <div><Label className="text-xs text-slate-500">GSTIN</Label><p>{detail.gstin || '-'}</p></div>
                  <div><Label className="text-xs text-slate-500">PAN</Label><p>{detail.pan || '-'}</p></div>
                  <div><Label className="text-xs text-slate-500">Onboarded By</Label><p>{detail.onboarded_by_name || '-'}</p></div>
                  <div><Label className="text-xs text-slate-500">Onboarded At</Label><p>{detail.onboarded_at ? new Date(detail.onboarded_at).toLocaleString() : '-'}</p></div>
                </div>
                <div>
                  <p className="font-semibold mb-2 text-sm">Deployed Assets ({detail.deployed_assets?.length || 0})</p>
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {(detail.deployed_assets || []).map(a => (
                      <div key={a.id} className="p-2 bg-slate-50 rounded flex justify-between text-xs">
                        <span className="font-mono">{a.serial_number}</span>
                        <span className={`px-2 py-0.5 rounded ${a.status === 'deployed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{a.status}</span>
                      </div>
                    ))}
                    {(!detail.deployed_assets || detail.deployed_assets.length === 0) && <p className="text-xs text-slate-500 text-center py-3">No assets deployed yet</p>}
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Merchants;
